import { useState, useRef, useEffect } from 'react';
import { MdArrowUpward, MdArrowDownward, MdClose, MdCloudUpload, MdExpandMore, MdImage, MdMusicNote, MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';
import GENRES from '../../utils/Genres';
import RightsInputs, { EMPTY_RIGHTS, hasRights, appendRights } from './RightsInputs';
import CreditsInputs from './CreditsInputs';
import { AUDIO_ACCEPT, AUDIO_FORMATS_HINT, AUDIO_QUALITY_HINT, audioFileProblem, uploadTrackAudio } from '../../utils/audioUpload';

// Mirrors the "Faixas" step of the web app's studio wizard, for a release that
// already exists: drop several files, fill each row, send them one by one.

// The audio goes straight to S3 (utils/audioUpload.js); only the cover still
// travels with the insert request.
const COVER_MAX_MB = 5;
const ISRC_PATTERN = /^[A-Z]{2}-?[A-Z0-9]{3}-?[0-9]{2}-?[0-9]{5}$/;
const VERSIONS = [['original', 'Original'], ['remix', 'Remix'], ['cover', 'Cover'], ['live', 'Ao vivo']];
const TYPE_LABELS = { album: 'Álbum', ep: 'EP', single: 'Single' };

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

const extOf = (name) => (name.split('.').pop() || '').toLowerCase();
const titleFromFile = (name) => name.replace(/\.[^.]+$/, '').replace(/[_]+/g, ' ').trim();
const formatMs = (ms) => {
    if (!ms) return '–:––';
    const total = Math.round(ms / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};
const formatBytes = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const linkProblem = (link) => {
    const v = link.trim();
    return v === '' || /^https?:\/\/\S+\.\S+/i.test(v) ? null : 'Começa por https://';
};
const isrcProblem = (t) => {
    if (!t.isrcMode) return 'Diz se já tens o código ISRC desta faixa.';
    if (t.isrcMode === 'existing') {
        if (!t.isrc.trim()) return 'Insere o ISRC que a faixa já tem.';
        if (!ISRC_PATTERN.test(t.isrc.trim().toUpperCase())) return 'ISRC inválido. Formato: CC-XXX-YY-NNNNN.';
    }
    return null;
};

/** What a release with this many tracks is usually called. A suggestion only. */
const suggestType = (n) => (n <= 1 ? 'single' : n <= 6 ? 'ep' : 'album');

let nextKey = 1;

function readDuration(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const audio = new Audio();
        const done = (ms) => { URL.revokeObjectURL(url); resolve(ms); };
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => done(Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : 0);
        audio.onerror = () => done(0);
        audio.src = url;
    });
}

/**
 * Props:
 *  release        the release as release/fetch.php returns it (with tracks)
 *  onUploaded()   called after each stored track, to refresh the list
 *  onChangeType(type)  switches the release's type
 *  onPickExisting()    toggles the picker of songs already uploaded
 *  pickingExisting     whether that picker is open
 *  children       the picker itself, shown under the dropzone
 */
export default function ReleaseTrackUploader({ release, onUploaded, onChangeType, onPickExisting, pickingExisting, children }) {
    const fileInput = useRef(null);
    const coverInput = useRef(null);
    const [rows, setRows] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [rejected, setRejected] = useState([]);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState('');
    const [coverError, setCoverError] = useState('');

    const existing = release.tracks || [];
    const artistId = Number(release.artist_id);

    // A cover already stored for one of this artist's tracks in the release is
    // reused, so the image is not uploaded once per track.
    const storedCoverPrefix = `/users/${artistId}/songs/artworks/`;
    const reusableCover = (() => {
        const t = existing.find((x) => typeof x.cover === 'string' && x.cover.includes(storedCoverPrefix));
        return t ? t.cover.split('/').pop() : null;
    })();
    const [sessionCover, setSessionCover] = useState(null);
    const coverName = sessionCover || reusableCover;

    useEffect(() => () => { if (coverPreview) URL.revokeObjectURL(coverPreview); }, [coverPreview]);

    const patch = (key, p) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...p } : r)));

    const addFiles = async (files) => {
        const bad = [];
        const good = [];
        for (const f of files) {
            const problem = audioFileProblem(f);
            if (problem) bad.push(problem);
            else good.push(f);
        }
        setRejected(bad);
        if (good.length === 0) return;
        // Folder order is usually track order.
        good.sort((a, b) => a.name.localeCompare(b.name, 'pt', { numeric: true }));
        const added = good.map((file) => ({
            key: `t${nextKey++}`,
            file,
            title: titleFromFile(file.name),
            explicit: false,
            // Left unanswered on purpose, as in the single upload: a wrong default
            // would give a released track a second code.
            isrcMode: '',
            isrc: '',
            version: 'original',
            genre: '',
            tags: '',
            link: '',
            rights: EMPTY_RIGHTS,
            credits: [],
            durationMs: 0,
            status: 'idle', // idle | sending | error
            progress: 0,
            message: '',
        }));
        setRows((prev) => [...prev, ...added]);
        setError('');
        for (const r of added) {
            readDuration(r.file).then((ms) => patch(r.key, { durationMs: ms }));
        }
    };

    const move = (key, dir) => setRows((prev) => {
        const i = prev.findIndex((r) => r.key === key);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        const next = [...prev];
        [next[i], next[j]] = [next[j], next[i]];
        return next;
    });

    const remove = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

    const toggle = (key) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const pickCover = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setCoverError('');
        if (!file) return;
        if (!['png', 'jpg', 'jpeg'].includes(extOf(file.name))) { setCoverError('Formato não suportado. Usa JPG ou PNG.'); return; }
        if (file.size > COVER_MAX_MB * 1024 * 1024) { setCoverError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${COVER_MAX_MB} MB.`); return; }
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
    };

    const validate = () => {
        if (rows.length === 0) return 'Junta pelo menos uma faixa.';
        if (rows.some((r) => !r.title.trim())) return 'Todas as faixas precisam de nome.';
        if (rows.some((r) => r.title.trim().length > 200)) return 'Há um nome de faixa demasiado longo.';
        const badIsrc = rows.find((r) => isrcProblem(r));
        if (badIsrc) return `"${badIsrc.title.trim()}": ${isrcProblem(badIsrc)}`;
        if (!release.genre && rows.some((r) => !r.genre)) return 'Este lançamento não tem género: escolhe-o em "Editar detalhes", ou um género para cada faixa em "Mais detalhes".';
        const badLink = rows.find((r) => linkProblem(r.link));
        if (badLink) return `O link de "${badLink.title.trim()}" tem de começar por https://.`;
        if (!coverName && !coverFile) return 'Escolhe a capa das novas faixas.';
        return null;
    };

    // Sends the rows one at a time, in order. A stored row leaves the list, so
    // after a failure "Tentar novamente" carries on from the one that failed.
    const send = async () => {
        const problem = validate();
        if (problem) { setShowErrors(true); setError(problem); return; }
        setError('');
        setSending(true);
        let cover = coverName;
        let position = existing.length;
        const queue = [...rows];
        for (const row of queue) {
            position += 1;
            patch(row.key, { status: 'sending', progress: 0, message: '' });
            const fd = new FormData();
            fd.append('user_id', artistId);
            fd.append('title', row.title.trim());
            fd.append('genre', row.genre || release.genre || '');
            fd.append('tags', row.tags.trim());
            fd.append('country', 'Angola');
            fd.append('link', row.link.trim());
            appendRights(fd, row.rights);
            fd.append('credits', JSON.stringify(row.credits));
            fd.append('version', row.version || 'original');
            fd.append('explicit', row.explicit ? '1' : '0');
            fd.append('content_category', 'music');
            fd.append('isrc', row.isrcMode === 'existing' ? row.isrc.trim().toUpperCase() : '');
            fd.append('isrc_pending', row.isrcMode === 'izenu' ? '1' : '0');
            fd.append('duration', row.durationMs || '');
            fd.append('release_id', release.id);
            fd.append('track_number', position);
            if (cover) fd.append('cover', cover);
            else fd.append('cover_file', coverFile);
            try {
                // The audio first, straight to S3. Kept on the row, so a retry
                // after a failed insert does not send the file again.
                let musicFile = row.uploaded;
                if (!musicFile) {
                    musicFile = await uploadTrackAudio(row.file, {
                        artistId,
                        onProgress: (p) => patch(row.key, { progress: p }),
                    });
                    row.uploaded = musicFile;
                    patch(row.key, { uploaded: musicFile });
                }
                fd.append('music_file', musicFile);
                // No manual Content-Type: axios has to set the multipart boundary itself.
                const { data: r } = await izenuAPI.post('/distributor/artist/insertMusic.php', fd);
                if (!r.response) throw new Error(r.message || 'O servidor recusou a faixa.');
                if (!cover && r.cover) { cover = r.cover; setSessionCover(r.cover); }
                setRows((prev) => prev.filter((x) => x.key !== row.key));
                onUploaded();
            } catch (err) {
                const message = err?.response?.data?.message || err.message || 'Falha no envio. Verifica a ligação.';
                patch(row.key, { status: 'error', message });
                setError(`Não foi possível carregar "${row.title.trim()}". As anteriores ficaram guardadas; tenta novamente para continuar.`);
                setSending(false);
                return;
            }
        }
        setSending(false);
        setShowErrors(false);
    };

    const totalAfter = existing.length + rows.length;
    const suggested = suggestType(totalAfter);
    const suggestion = rows.length > 0 && release.type !== 'single' && suggested !== 'single' && suggested !== release.type ? suggested : null;
    const anyFailed = rows.some((r) => r.status === 'error');

    const compact = existing.length > 0 || rows.length > 0;
    const kindLabel = release.type === 'ep' ? 'EP' : 'álbum';

    return (
        <div>
            {/* Rows waiting to be sent, numbered after the stored tracks */}
            {rows.length > 0 && (
                <div className="border-t bg-amber-50/40">
                    <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-amber-800">
                        Por carregar · {rows.length} {rows.length === 1 ? 'faixa' : 'faixas'}
                    </p>

                    {suggestion && (
                        <div className="mx-4 mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg !border !border-solid border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                            <span className="text-amber-900">Com {totalAfter} faixas, isto costuma chamar-se {TYPE_LABELS[suggestion]}.</span>
                            <button type="button" onClick={() => onChangeType(suggestion)} className="font-medium text-amber-800 hover:underline">
                                Mudar para {TYPE_LABELS[suggestion]}
                            </button>
                        </div>
                    )}

                    {!coverName && (
                        <div className="mx-4 mt-2 flex items-center gap-3 rounded-lg !border !border-solid border-gray-200 bg-white px-3 py-2.5">
                            <input ref={coverInput} type="file" accept=".png,.jpg,.jpeg" className="hidden" onChange={pickCover} />
                            <button type="button" onClick={() => coverInput.current?.click()} disabled={sending} aria-label="Escolher capa"
                                className={`flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100 ${showErrors && !coverFile ? 'ring-2 ring-red-400' : ''}`}>
                                {coverPreview ? <img src={coverPreview} alt="" className="h-full w-full object-cover" /> : <MdImage size={22} className="text-gray-400" />}
                            </button>
                            <div className="min-w-0 text-left">
                                <p className="text-sm font-medium text-gray-900">Capa das faixas <span className="text-red-500">*</span></p>
                                <p className="text-xs text-gray-500">Ainda não há uma capa de faixa para reutilizar neste {kindLabel}. JPG ou PNG até {COVER_MAX_MB} MB, serve para todas.</p>
                                {coverError && <p className="mt-0.5 text-xs text-red-600">{coverError}</p>}
                            </div>
                        </div>
                    )}

                    <ol className="space-y-2 p-4 [&>li]:!p-0">
                        {rows.map((t, i) => {
                            const filled = (t.version && t.version !== 'original') || !!t.genre || !!t.tags || !!t.link || hasRights(t.rights);
                            const open = filled || expanded.has(t.key);
                            const isrcErr = showErrors ? isrcProblem(t) : null;
                            const linkErr = linkProblem(t.link);
                            const locked = sending;
                            const number = existing.length + i + 1;
                            return (
                                <li key={t.key}>
                                    <div className={`rounded-lg !border !border-solid bg-white p-3 sm:p-4 ${t.status === 'error' ? 'border-red-300' : 'border-gray-200'}`}>
                                        {/* Line 1: number, name, explicit, controls */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                            <span className="w-5 shrink-0 text-center text-sm tabular-nums text-gray-400">{number}</span>
                                            <div className="relative min-w-0 flex-1 basis-56">
                                                <MdMusicNote size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input aria-label={`Nome da faixa ${number}`} className={`${inputCls} !pl-9 ${!t.title.trim() ? '!border-red-400' : ''}`}
                                                    value={t.title} maxLength={200} disabled={locked} placeholder="Nome da faixa"
                                                    onChange={(e) => patch(t.key, { title: e.target.value })} />
                                            </div>
                                            <label className="flex shrink-0 cursor-pointer items-center gap-2">
                                                <input type="checkbox" className="peer sr-only" checked={t.explicit} disabled={locked}
                                                    onChange={(e) => patch(t.key, { explicit: e.target.checked })} />
                                                <span aria-hidden="true" className={`relative h-5 w-9 rounded-full transition ${t.explicit ? 'bg-amber-500' : 'bg-gray-300'} peer-focus-visible:ring-2 peer-focus-visible:ring-amber-400`}>
                                                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${t.explicit ? 'left-[18px]' : 'left-0.5'}`} />
                                                </span>
                                                <span className="whitespace-nowrap text-xs text-gray-700">Explícita</span>
                                            </label>
                                            <div className="ms-auto flex shrink-0 items-center">
                                                <button type="button" disabled={locked || i === 0} onClick={() => move(t.key, -1)} aria-label="Subir" title="Subir"
                                                    className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowUpward size={16} /></button>
                                                <button type="button" disabled={locked || i === rows.length - 1} onClick={() => move(t.key, 1)} aria-label="Descer" title="Descer"
                                                    className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowDownward size={16} /></button>
                                                <button type="button" disabled={locked} onClick={() => remove(t.key)} aria-label="Tirar da lista" title="Tirar da lista"
                                                    className="rounded-full p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600 disabled:text-gray-200"><MdClose size={18} /></button>
                                            </div>
                                        </div>

                                        <div className="mt-1.5 space-y-3 ps-8">
                                            <p className="truncate text-xs text-gray-500" title={t.file.name}>
                                                {!t.title.trim() && <span className="text-red-600">A faixa precisa de um nome · </span>}
                                                {formatMs(t.durationMs)} · {formatBytes(t.file.size)} · {t.file.name}
                                            </p>

                                            {/* ISRC */}
                                            <div>
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                                                    <span className="text-sm text-gray-700"
                                                        title="O ISRC identifica a gravação em todas as plataformas e é por ele que os royalties chegam ao artista.">
                                                        Tens o código ISRC desta música? <span className="text-red-500">*</span>
                                                    </span>
                                                    <div className={`inline-flex overflow-hidden rounded-full !border !border-solid ${isrcErr && !t.isrcMode ? 'border-red-400' : 'border-gray-300'}`} role="radiogroup">
                                                        {[['existing', 'Sim'], ['izenu', 'Não']].map(([value, label]) => (
                                                            <button key={value} type="button" role="radio" aria-checked={t.isrcMode === value} disabled={locked}
                                                                onClick={() => patch(t.key, { isrcMode: value })}
                                                                className={`px-4 py-1 text-xs font-medium ${t.isrcMode === value ? 'bg-amber-500 !text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {t.isrcMode === 'existing' && (
                                                        <input aria-label="ISRC" className={`${inputCls} !w-48 !py-1`} value={t.isrc} disabled={locked} placeholder="AO-Z03-24-00001"
                                                            onChange={(e) => patch(t.key, { isrc: e.target.value })} />
                                                    )}
                                                </div>
                                                {t.isrcMode === 'existing' && !isrcErr && <p className="mt-1 text-xs text-gray-500">O mesmo código das outras plataformas: um segundo dividia as reproduções e os royalties.</p>}
                                                {t.isrcMode === 'izenu' && <p className="mt-1 text-xs text-gray-500">Sem problema: a Izenu gera um ISRC para esta faixa.</p>}
                                                {isrcErr && <p className="mt-1 text-xs text-red-600">{isrcErr}</p>}
                                            </div>

                                            <div className="border-t pt-3">
                                                <CreditsInputs value={t.credits} disabled={locked}
                                                    mainArtist={release.artist_display_name || release.artist_name}
                                                    onChange={(credits) => patch(t.key, { credits })}
                                                    onCopyPrevious={i > 0 && rows[i - 1].credits.length > 0
                                                        ? () => patch(t.key, { credits: rows[i - 1].credits.map((c) => ({ ...c })) })
                                                        : undefined} />
                                            </div>

                                            {/* Optional details */}
                                            {!filled && (
                                                <button type="button" onClick={() => toggle(t.key)} aria-expanded={open}
                                                    className="flex items-center gap-1 text-xs text-amber-700 hover:underline">
                                                    <MdExpandMore size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                                                    {open ? 'Menos detalhes' : 'Mais detalhes: versão, género, tags, link e direitos de autor'}
                                                </button>
                                            )}
                                            {open && (
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                    <label className="block">
                                                        <span className="mb-1 block text-xs font-medium text-gray-700">Versão</span>
                                                        <select className={inputCls} value={t.version} disabled={locked} onChange={(e) => patch(t.key, { version: e.target.value })}>
                                                            {VERSIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                                        </select>
                                                    </label>
                                                    <label className="block">
                                                        <span className="mb-1 block text-xs font-medium text-gray-700">Género</span>
                                                        <select className={inputCls} value={t.genre} disabled={locked} onChange={(e) => patch(t.key, { genre: e.target.value })}>
                                                            <option value="">{release.genre ? `Igual ao ${kindLabel} (${release.genre})` : `Igual ao ${kindLabel}`}</option>
                                                            {GENRES.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
                                                        </select>
                                                    </label>
                                                    <label className="block">
                                                        <span className="mb-1 block text-xs font-medium text-gray-700">Tags</span>
                                                        <input className={inputCls} value={t.tags} maxLength={200} disabled={locked} placeholder="kizomba, romântica"
                                                            onChange={(e) => patch(t.key, { tags: e.target.value })} />
                                                    </label>
                                                    <label className="block">
                                                        <span className="mb-1 block text-xs font-medium text-gray-700">Link</span>
                                                        <input className={`${inputCls} ${linkErr ? '!border-red-400' : ''}`} value={t.link} maxLength={255} disabled={locked}
                                                            placeholder="https://… (ex.: o vídeo)" onChange={(e) => patch(t.key, { link: e.target.value })} />
                                                        {linkErr && <span className="mt-1 block text-xs text-red-600">{linkErr}</span>}
                                                    </label>
                                                </div>
                                            )}
                                            {open && (
                                                <RightsInputs small value={t.rights} disabled={locked}
                                                    holderPlaceholder={release.artist_display_name || release.artist_name}
                                                    labelPlaceholder={localStorage.getItem('name') || ''}
                                                    onChange={(p) => patch(t.key, { rights: { ...t.rights, ...p } })} />
                                            )}

                                            {t.status === 'sending' && (
                                                <div>
                                                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-200">
                                                        <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${t.progress}%` }} />
                                                    </div>
                                                    <p className="mt-1 text-xs text-gray-600">{t.progress < 100 ? `A enviar… ${t.progress}%` : 'A processar no servidor…'}</p>
                                                </div>
                                            )}
                                            {t.status === 'error' && (
                                                <p className="flex items-start gap-1 text-xs text-red-600"><MdErrorOutline size={14} className="mt-px shrink-0" /> {t.message}</p>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            )}

            {/* Add: drop new files, or pick songs already uploaded */}
            <div className="border-t p-4">
                <input ref={fileInput} type="file" multiple accept={AUDIO_ACCEPT} className="hidden"
                    onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ''; }} />
                <button type="button" disabled={sending}
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); if (!sending) addFiles(Array.from(e.dataTransfer.files || [])); }}
                    className={`flex w-full items-center justify-center rounded-xl !border-2 !border-dashed transition-colors disabled:opacity-60 ${compact ? 'gap-3 px-4 py-4' : 'flex-col gap-1.5 px-6 py-10'} ${dragging ? '!border-amber-400 bg-amber-50' : '!border-gray-300 bg-white hover:!border-amber-400 hover:bg-amber-50/40'}`}>
                    <MdCloudUpload size={compact ? 24 : 32} className="shrink-0 text-gray-400" />
                    <span className={compact ? 'text-left' : 'text-center'}>
                        <span className="block font-medium text-gray-900">{compact ? 'Juntar faixas' : 'Larga as faixas aqui'}</span>
                        <span className="block text-xs text-gray-500">Arrasta ficheiros ou clica para escolher. {AUDIO_FORMATS_HINT} Podes largar a pasta inteira.</span>
                        <span className="block text-xs text-gray-400">{AUDIO_QUALITY_HINT}</span>
                    </span>
                </button>
                {rejected.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-red-600">
                        {rejected.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                )}
                <div className="mt-2 text-center">
                    <button type="button" onClick={onPickExisting} disabled={sending} className="text-sm text-amber-700 hover:underline disabled:opacity-50">
                        {pickingExisting ? 'Fechar a lista de músicas já carregadas' : 'Ou escolhe de músicas que o artista já carregou'}
                    </button>
                </div>
                {children}
            </div>

            {/* Send bar: stays in view while the rows are being filled */}
            {rows.length > 0 && (
                <div className="sticky bottom-0 z-10 flex flex-col gap-2 rounded-b-xl border-t bg-white/95 px-4 py-3 backdrop-blur sm:flex-row sm:items-center">
                    <p className={`min-w-0 flex-1 text-sm ${error ? 'text-red-600' : 'text-gray-600'}`}>
                        {error || (sending ? 'A enviar as faixas, uma de cada vez. Não feches esta página.' : `${rows.length} ${rows.length === 1 ? 'faixa pronta' : 'faixas prontas'} para carregar neste ${kindLabel}.`)}
                    </p>
                    <div className="flex shrink-0 gap-2">
                        {!sending && (
                            <button type="button" onClick={() => { setRows([]); setError(''); setShowErrors(false); }}
                                className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                                Descartar
                            </button>
                        )}
                        <button type="button" onClick={send} disabled={sending}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-60 sm:flex-none">
                            {sending ? 'A carregar…' : anyFailed ? 'Tentar novamente' : <><MdCheckCircle size={16} /> Carregar {rows.length} {rows.length === 1 ? 'faixa' : 'faixas'}</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
